/** MIDI 1.0 channel-voice message parsing. Channels are 1–16 in the public API. */
export type MidiEvent =
  | { type: "noteon"; channel: number; note: number; velocity: number } // velocity 1–127
  | { type: "noteoff"; channel: number; note: number; velocity: number }
  | { type: "cc"; channel: number; controller: number; value: number }
  | { type: "pitchbend"; channel: number; value: number } // -8192..8191
  | { type: "program"; channel: number; program: number }
  | { type: "aftertouch"; channel: number; pressure: number }
  | { type: "polyaftertouch"; channel: number; note: number; pressure: number }
  | { type: "system"; status: number; data: number[] }
  | { type: "unknown"; data: number[] };

export const CC = {
  MOD_WHEEL: 1,
  VOLUME: 7,
  SUSTAIN: 64,
  ALL_SOUND_OFF: 120,
  RESET_ALL_CONTROLLERS: 121,
  ALL_NOTES_OFF: 123,
} as const;

export function parseMidiMessage(data: ArrayLike<number>): MidiEvent {
  const bytes = Array.from(data);
  const status = bytes[0];
  if (status === undefined || status < 0x80) return { type: "unknown", data: bytes };
  if (status >= 0xf0) return { type: "system", status, data: bytes.slice(1) };
  const kind = status & 0xf0;
  const channel = (status & 0x0f) + 1;
  const d1 = (bytes[1] ?? 0) & 0x7f;
  const d2 = (bytes[2] ?? 0) & 0x7f;
  switch (kind) {
    case 0x80: return { type: "noteoff", channel, note: d1, velocity: d2 };
    // Running convention: note-on with velocity 0 means note-off.
    case 0x90: return d2 === 0 ? { type: "noteoff", channel, note: d1, velocity: 0 } : { type: "noteon", channel, note: d1, velocity: d2 };
    case 0xa0: return { type: "polyaftertouch", channel, note: d1, pressure: d2 };
    case 0xb0: return { type: "cc", channel, controller: d1, value: d2 };
    case 0xc0: return { type: "program", channel, program: d1 };
    case 0xd0: return { type: "aftertouch", channel, pressure: d1 };
    case 0xe0: return { type: "pitchbend", channel, value: ((d2 << 7) | d1) - 8192 };
    default: return { type: "unknown", data: bytes };
  }
}

function statusByte(kind: number, channel: number): number {
  if (!Number.isInteger(channel) || channel < 1 || channel > 16) throw new RangeError(`MIDI channel out of range: ${channel}`);
  return kind | (channel - 1);
}

export function noteOnMessage(channel: number, note: number, velocity: number): number[] {
  return [statusByte(0x90, channel), note & 0x7f, velocity & 0x7f];
}

export function noteOffMessage(channel: number, note: number, velocity = 0): number[] {
  return [statusByte(0x80, channel), note & 0x7f, velocity & 0x7f];
}

export function ccMessage(channel: number, controller: number, value: number): number[] {
  return [statusByte(0xb0, channel), controller & 0x7f, value & 0x7f];
}

/** CC120 (All Sound Off) + CC123 (All Notes Off) on all 16 channels = 32 messages. */
export function panicMessages(): number[][] {
  const out: number[][] = [];
  for (let ch = 1; ch <= 16; ch++) {
    out.push(ccMessage(ch, CC.ALL_SOUND_OFF, 0), ccMessage(ch, CC.ALL_NOTES_OFF, 0));
  }
  return out;
}

/** Short human-readable description for the MIDI monitor. */
export function describeMidiEvent(e: MidiEvent): string {
  switch (e.type) {
    case "noteon": return `Note On ch${e.channel} note ${e.note} vel ${e.velocity}`;
    case "noteoff": return `Note Off ch${e.channel} note ${e.note}`;
    case "cc": return `CC ch${e.channel} #${e.controller} = ${e.value}${e.controller === CC.SUSTAIN ? " (sustain)" : ""}`;
    case "pitchbend": return `Pitch Bend ch${e.channel} ${e.value}`;
    case "program": return `Program ch${e.channel} ${e.program}`;
    case "aftertouch": return `Aftertouch ch${e.channel} ${e.pressure}`;
    case "polyaftertouch": return `Poly AT ch${e.channel} note ${e.note} ${e.pressure}`;
    case "system": return `System 0x${e.status.toString(16)}`;
    default: return `Unknown [${e.data.join(" ")}]`;
  }
}
